export function maskPhone(value) {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2');
}

export function maskCPF(value) {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
}

/**
 * Normaliza CNPJ (numérico ou alfanumérico): maiúsculas, sem máscara.
 * As 12 primeiras posições aceitam A–Z/0–9; os 2 DVs são apenas dígitos.
 */
export function normalizeCNPJ(value) {
    const upper = String(value ?? '').toUpperCase();
    let bare = '';
    for (const ch of upper) {
        if (bare.length >= 14) break;
        if (bare.length < 12) {
            if (/[A-Z0-9]/.test(ch)) bare += ch;
        } else if (/\d/.test(ch)) {
            bare += ch;
        }
    }
    return bare;
}

/**
 * Máscara AA.AAA.AAA/AAAA-00 — compatível com CNPJ numérico e alfanumérico.
 */
export function maskCNPJ(value) {
    const c = normalizeCNPJ(value);
    const p1 = c.slice(0, 2);
    const p2 = c.slice(2, 5);
    const p3 = c.slice(5, 8);
    const p4 = c.slice(8, 12);
    const p5 = c.slice(12, 14);

    let out = p1;
    if (p2) out += `.${p2}`;
    if (p3) out += `.${p3}`;
    if (p4) out += `/${p4}`;
    if (p5) out += `-${p5}`;
    return out;
}

export function maskZipCode(value) {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{3})\d+?$/, '$1');
}

export function maskDate(value) {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{4})\d+?$/, '$1');
}

/**
 * Formata data ISO (YYYY-MM-DD) para exibição em pt-BR: DD/MM/YYYY
 * @param {string} isoDate - Data no formato YYYY-MM-DD
 * @returns {string} Data no formato DD/MM/YYYY ou '' se inválida
 */
export function formatDateBR(isoDate) {
    if (!isoDate || typeof isoDate !== 'string') return '';
    const trimmed = isoDate.trim();
    if (trimmed.length < 10) return '';
    const [y, m, d] = [trimmed.slice(0, 4), trimmed.slice(5, 7), trimmed.slice(8, 10)];
    if (!d || !m || !y) return '';
    return `${d}/${m}/${y}`;
}

/**
 * Converte data em pt-BR (DD/MM/YYYY) para ISO (YYYY-MM-DD)
 * @param {string} value - Texto no formato DD/MM/YYYY (com ou sem barras)
 * @returns {string} Data no formato YYYY-MM-DD ou '' se incompleta/inválida
 */
export function parseDateBR(value) {
    if (!value || typeof value !== 'string') return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length < 8) return '';
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    if (y.length < 4) return '';
    const day = parseInt(d, 10);
    const month = parseInt(m, 10);
    const year = parseInt(y, 10);
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1) return '';
    return `${y.padStart(4, '0')}-${m}-${d}`;
}

/**
 * Formata número como moeda em Real (Brasil): R$ 1.234,56
 * @param {number|null|undefined} value - Valor numérico
 * @returns {string} "R$ 0,00" ou "R$ 1.234,56" ou "" se value for vazio
 */
export function formatCurrencyBR(value) {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return num.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/**
 * Converte string de valor em Real (1.234,56 ou 1234,56) para número
 * @param {string} value - Texto digitado (pode conter R$, pontos de milhar, vírgula decimal)
 * @returns {number|null} Valor numérico ou null se vazio/inválido
 */
export function parseCurrencyBR(value) {
    if (value === null || value === undefined) return null;
    const str = String(value).trim().replace(/\s/g, '');
    if (str === '' || str === 'R$') return null;
    const normalized = str.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    const num = parseFloat(normalized);
    return Number.isNaN(num) ? null : num;
}

/**
 * Interpreta apenas os dígitos do input como centavos (preenchimento da direita para a esquerda).
 * Ex.: "4" -> 0,04 | "45" -> 0,45 | "4500" -> 45,00
 * @param {string} value - Texto do input (só os dígitos são considerados)
 * @param {number} maxDigits - Máximo de dígitos (padrão 14 = até 999.999.999.999,99)
 * @returns {number|null} Valor em reais ou null se vazio
 */
export function parseCurrencyCentsFromInput(value, maxDigits = 14) {
    if (value === null || value === undefined) return null;
    const digits = String(value).replace(/\D/g, '').slice(0, maxDigits);
    if (digits === '') return null;
    const cents = parseInt(digits, 10);
    return Number.isNaN(cents) ? null : cents / 100;
}

/**
 * Formata número como porcentagem para exibição com 2 casas decimais: 10.5 → "10,50"
 */
export function formatPercentage(value) {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Converte string de porcentagem (ex.: "10,5" ou "10.5") para número
 */
export function parsePercentage(value) {
    if (value === null || value === undefined) return null;
    const str = String(value).trim().replace(/\s/g, '').replace(',', '.');
    if (str === '' || str === '%') return null;
    const num = parseFloat(str.replace(/[^\d.-]/g, ''));
    return Number.isNaN(num) ? null : num;
}
